import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTaxonomyList } from './useSectorTaxonomy';
import {
  bulkMergeMappings,
  type SectorMappingItem,
} from '../../api/sectorTaxonomyApi';
import { ApiError } from '../../api/client';
import { CanonicalSelect } from './CanonicalSelect';

interface Props {
  selected: SectorMappingItem[];
  defaultReviewer: string;
  onClose: () => void;
  /**
   * 머지 성공 시 호출 — 부모(MappingsTab) 에서 invalidate + toast.
   * @param mergedCount 실제 머지 row 수 (skipped 제외).
   * @param targetCanonical 통합된 canonical_sector_id.
   * @param skippedCount  row=0 (이미 동일 canonical 등) 였던 건수.
   */
  onSuccess: (mergedCount: number, targetCanonical: string, skippedCount: number) => void;
  onError: (msg: string, tone?: 'info' | 'success' | 'error' | 'warn') => void;
  /** 422 정책 3 위반 — uncovered sample 표시는 모달 내부에서 처리하지만 외부 toast 도 함께. */
  onPolicyViolation?: (uncoveredCount: number, sampleSize: number) => void;
}

// 일괄 머지 모달 — 화면 11 매핑 테이블 다중 선택 → 단일 canonical 통합.
// target drop-down 은 useTaxonomyList(isActive=true) + sec_l1_meta_* 제외.
// 백엔드는 단일 mergeInto SQL 을 반복하면서 정책 3 (uncovered coverage) 가드를
// 트랜잭션 끝에서 검증한다. 위반 시 422 + 롤백 (매핑 변경 0건) — 모달은 안내만.
export function BulkMergeModal({
  selected,
  defaultReviewer,
  onClose,
  onSuccess,
  onError,
  onPolicyViolation,
}: Props): JSX.Element {
  const taxonomy = useTaxonomyList({ isActive: true });
  const [target, setTarget] = useState('');
  const [reviewer, setReviewer] = useState(defaultReviewer);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // 422 응답 시 표시할 uncovered_symbols_sample 메타.
  // sample 은 LinkedHashMap[] 형태(symbol, source, source_sector_name). cap 50.
  const [uncoveredSample, setUncoveredSample] = useState<unknown[] | null>(null);
  const [uncoveredCount, setUncoveredCount] = useState<number | null>(null);

  // 메타 L1 (sec_l1_meta_*) 제외는 CanonicalSelect 의 excludeMetaL1 기본값으로 처리.

  async function handleSubmit(): Promise<void> {
    if (busy) return;
    if (!target.trim()) {
      onError('통합 대상 canonical 을 선택하세요.', 'warn');
      return;
    }
    if (!reviewer.trim()) {
      onError('reviewer 가 비어있습니다.', 'warn');
      return;
    }
    if (selected.length === 0) {
      onError('선택된 매핑이 없습니다.', 'warn');
      return;
    }
    setBusy(true);
    setUncoveredSample(null);
    setUncoveredCount(null);
    try {
      const resp = await bulkMergeMappings({
        mappingIds: selected.map((s) => s.mappingId),
        targetCanonical: target.trim(),
        reviewer: reviewer.trim(),
        note: note.trim() || undefined,
      });
      onSuccess(resp.mergedCount, resp.targetCanonical, resp.skippedCount);
    } catch (err) {
      if (err instanceof ApiError) {
        // 422 정책 3 위반 — meta.uncovered_symbols_sample 노출.
        if (err.httpStatus === 422) {
          const m = err.meta ?? {};
          const sample = Array.isArray(m['uncovered_symbols_sample'])
            ? (m['uncovered_symbols_sample'] as unknown[])
            : [];
          const count =
            typeof m['uncovered_count'] === 'number'
              ? (m['uncovered_count'] as number)
              : sample.length;
          setUncoveredSample(sample);
          setUncoveredCount(count);
          onPolicyViolation?.(count, sample.length);
          onError(
            `정책 3 위반 — uncovered=${count} 건. 매핑 변경 없이 롤백되었습니다.`,
            'error'
          );
          return;
        }
        // 400 메타 L1 target / mapping_ids_empty / target_canonical_missing.
        if (err.httpStatus === 400 && err.code === 'invalid_target') {
          onError('메타 L1 canonical 은 target 으로 사용할 수 없습니다.', 'error');
          return;
        }
        onError(`머지 실패: ${err.message}`, 'error');
        return;
      }
      const msg = err instanceof Error ? err.message : 'bulk_merge_failed';
      onError(`머지 실패: ${msg}`, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tx-modal-backdrop" role="dialog" aria-modal="true">
      <div className="tx-modal" style={{ maxWidth: 720 }}>
        <h3 className="tx-modal__title">매핑 일괄 머지</h3>
        <div className="tx-modal__body">
          선택된 매핑 <strong>{selected.length}</strong> 건을 단일 canonical 섹터로
          통합합니다. 정책 3 (전 universe symbol coverage) 위반 시 매핑 변경 없이
          전체 롤백됩니다.
        </div>

        <div className="pca-filter" style={{ marginBottom: 0 }}>
          <div className="pca-filter__group" style={{ flex: 1 }}>
            <label className="pca-filter__label" htmlFor="bulk-merge-target">
              통합 대상 canonical (필수)
            </label>
            <CanonicalSelect
              inputId="bulk-merge-target"
              ariaLabel="통합 대상 canonical (필수)"
              value={target}
              onChange={setTarget}
              options={taxonomy.data ?? []}
              excludeMetaL1
              disabled={busy || taxonomy.isLoading}
            />
          </div>
        </div>

        <div className="pca-filter" style={{ marginTop: 6, marginBottom: 0 }}>
          <div className="pca-filter__group" style={{ flex: 1 }}>
            <label className="pca-filter__label" htmlFor="bulk-merge-reviewer">
              reviewer (필수)
            </label>
            <input
              id="bulk-merge-reviewer"
              className="pca-input"
              type="text"
              value={reviewer}
              onChange={(e) => setReviewer(e.target.value)}
              disabled={busy}
              placeholder="예: admin"
            />
          </div>
          <div className="pca-filter__group" style={{ flex: 2 }}>
            <label className="pca-filter__label" htmlFor="bulk-merge-note">
              note (선택)
            </label>
            <input
              id="bulk-merge-note"
              className="pca-input"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={busy}
              placeholder="예: 반도체 5종 L2 → sec_l1_semiconductor 통합"
            />
          </div>
        </div>

        {/* 선택 매핑 미리보기 (최대 10건 + 잔여 카운트) */}
        <div style={{ marginTop: 12 }}>
          <div className="pca-filter__label" style={{ marginBottom: 4 }}>
            선택된 매핑 미리보기
          </div>
          <div
            className="pca-table-wrap"
            style={{ maxHeight: 220, overflow: 'auto' }}
            data-testid="bulk-merge-preview"
          >
            <table className="pca-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Source</th>
                  <th>원천 섹터명</th>
                  <th>현재 canonical</th>
                </tr>
              </thead>
              <tbody>
                {selected.slice(0, 10).map((m) => (
                  <tr key={m.mappingId}>
                    <td>{m.mappingId}</td>
                    <td>{m.source}</td>
                    <td>{m.sourceSectorName}</td>
                    <td>
                      <span className="tx-tree__slug">{m.canonicalSectorId}</span>
                    </td>
                  </tr>
                ))}
                {selected.length > 10 && (
                  <tr>
                    <td colSpan={4} className="pca-muted">
                      … 외 {selected.length - 10} 건
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 422 정책 3 위반 시 sample 표시. 모달은 닫히지 않으며 매핑 변경 0건. */}
        {uncoveredSample !== null && (
          <div
            className="pca-warning-banner"
            data-testid="bulk-merge-uncovered"
            style={{ marginTop: 12 }}
          >
            <strong>
              정책 3 위반 — uncovered{' '}
              {uncoveredCount ?? uncoveredSample.length} 건. 매핑은 롤백됨.
            </strong>
            <ul style={{ marginTop: 6, fontSize: 12, maxHeight: 160, overflow: 'auto' }}>
              {uncoveredSample.slice(0, 20).map((s, i) => {
                const obj = (s ?? {}) as Record<string, unknown>;
                const sym = String(obj['symbol'] ?? obj['sourceSectorName'] ?? '');
                const src = String(obj['source'] ?? '');
                const name = String(obj['source_sector_name'] ?? obj['sourceSectorName'] ?? '');
                return (
                  <li key={i}>
                    {sym} <span className="pca-muted">— {src}:{name}</span>
                  </li>
                );
              })}
              {uncoveredSample.length > 20 && (
                <li className="pca-muted">… 외 {uncoveredSample.length - 20} 건</li>
              )}
            </ul>
          </div>
        )}

        <div className="tx-modal__actions">
          <button type="button" className="pca-btn" onClick={onClose} disabled={busy}>
            취소
          </button>
          <button
            type="button"
            className="pca-btn pca-btn--primary"
            onClick={handleSubmit}
            disabled={busy || !target.trim() || !reviewer.trim()}
          >
            {busy && <Loader2 size={12} strokeWidth={1.75} className="pca-spin" />}
            머지 실행
          </button>
        </div>
      </div>
    </div>
  );
}
